import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import posthog from 'posthog-js';
import { environment } from './../../../environments/environment';
import {DOCUMENT, isPlatformBrowser} from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class PosthogService {
  private initialized = false;
  private enabled = false;

  constructor(
    private ngZone: NgZone,
    private router: Router,
    @Inject(DOCUMENT) private doc: Document,
    @Inject(PLATFORM_ID) private platformId: any
  ) {}

  init(): void {
    if (this.initialized) {
      return;
    }
    this.enabled = this.shouldEnable();
    if (!this.enabled) {
      try {
        posthog.reset();
      } catch {}
      this.initialized = true;
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      posthog.init(environment.posthogKey, {
        api_host: environment.posthogHost,
        // Make flag checks non-blocking in slow networks
        feature_flag_request_timeout_ms: 3000,
      });
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(e => {
          posthog.capture('$pageview', {
            $current_url: window.location.href,
            $pathname: e.urlAfterRedirects,
          });
        });
    });

    this.initialized = true;
  }

  private shouldEnable(): boolean {
    if (!isPlatformBrowser(this.platformId)) { return false; }
    const host = this.doc?.location?.hostname || '';
    const hostAllowed = environment.allowedAnalyticsHosts?.includes(host);
    return Boolean(
      environment.production && environment.posthogKey && hostAllowed
    );
  }

  /** Identify the user after login */
  identifyUser(id: string, props?: Record<string, any>) {
    posthog.identify(id, props);
  }

  /** Reset identity on logout */
  reset() {
    posthog.reset();
  }

  /** Simple flag check (boolean or variant string) */
  getFlag(key: string): boolean | string | null {
    return posthog.getFeatureFlag(key) as any;
  }

  /** React to flag refreshes; useful when you need to wait for flags */
  onFlags(callback: () => void) {
    posthog.onFeatureFlags(callback);
  }

  /** Optional: control replay in code (you also enable it in Project Settings) */
  startReplay() {
    (posthog as any).startSessionRecording?.();
  }

  stopReplay() {
    (posthog as any).stopSessionRecording?.();
  }
}
